import type {RGB, RGBA} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {colorToRgbaArray} from '@Utils/color.util';
import {hexToRgb} from '@Utils/color.util';
import {convertFabricColorToFillObject, isSameColor, rgbFromRgba, rgbToHexString} from '@Utils/color.util';
import {convertFillObjectToFabricColor} from "@Utils/fabric.util";
import type {ItemObject, ItemType, SlideItemObject, TSerializedFabricColor} from '@PosterWhiteboard/items/item/item.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {DeepPartial} from '@/global';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {Point} from '@postermywall/fabricjs-2';
import type {TextStylesObject} from '@PosterWhiteboard/classes/text-styles.class';
import type {PageBackgroundObject} from '@PosterWhiteboard/page/page-background.class';
import {isImageBackground, isColorBackground} from '@PosterWhiteboard/page/background/background.types';
import {isSubset} from '@Utils/array.util';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {TextItem} from '../items/text-item/text-item.class';
import {LayoutBackgroundTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import type {TableItem, TableItemObject} from '@PosterWhiteboard/items/table-item/table-item.class';
import type {TabsItem, TabsItemObject} from '@PosterWhiteboard/items/tabs-item/tabs-item.class';
import {SeparatorType} from '@PosterWhiteboard/items/tabs-item/tabs-item.class';
import type {MenuItem, MenuItemObject} from '@PosterWhiteboard/items/menu-item/menu-item.class';
import type {QRCodeItem, QRCodeItemObject} from '@PosterWhiteboard/items/qr-code-item.class';
import type {FancyTextItem, FancyTextItemObject} from '@PosterWhiteboard/items/fancy-text-item/fancy-text-item.class';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import type {SubtitleObject} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import {findClosestColorInPaletteByLab, getAverageLabColor, getMostContrastingColorFromPalette, sortColorsByPerceptualLightness} from '@/utils/cielab-color.util';

const SHADOW_AREA_TO_ITEM_AREA_IMPORTANCE_RATIO = 0.25;

import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {updateLastAddedTextColor} from '@Components/poster-editor/library/poster-editor-library';

type ItemUpdateMap = Record<
  string,
  {
    updateObject: DeepPartial<ItemObject>;
    colorsUsedFromPalette: RGB[];
  }
>;

export const applySmartColorPalette = async (palette: ColorPaletteInfo): Promise<void> => {
  const paletteColors = palette.colors.map((hexColor) => hexToRgb(hexColor));

  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  const colorReplacements = getOnetoOneColorMappingsForNMostRepeatedColors(currentPage, paletteColors);

  const pageUpdate = getUpdateObjectAndUsedPaletteColorsForPageBackground(currentPage, colorReplacements, paletteColors);

  const itemUpdateMap: ItemUpdateMap = {}; // since the color changing operation in async,
  // we need to make and maintain a map of which colors will be APPLIED to an item, so that the item directly above it doesn't reuse the same color
  // (this effort maintains contrast)

  for (const item of currentPage.items.getItemsInOrder()) {
    const colorsBehindItem = getColorsBehindItem(item, currentPage, itemUpdateMap, pageUpdate.colorsUsedFromPalette);
    const filteredPalette = filterUsedColorsFromPalette(paletteColors, colorsBehindItem);

    const {updateObject, colorsUsedFromPalette} = getUpdateObjectAndUsedPaletteColorsForItem(item, colorReplacements, filteredPalette, paletteColors, {
      detectContrast: true,
      colorsBehind: colorsBehindItem,
    });

    itemUpdateMap[item.uid] = {updateObject, colorsUsedFromPalette};
  }

  await applyUpdates(currentPage, pageUpdate.updateObject, itemUpdateMap);
  await currentPage.updateFromObject(
    {
      appliedPalette: palette,
    },
    {undoable: false, updateRedux: false, fireModifiedEvent: false}
  );

  currentPage.poster.history.addPosterHistory();
  currentPage.fire('modified');
  currentPage.poster.redux.updateReduxData();
};

const getColorsOnCanvas = (n: number, page: Page): {allColors: string[]; nMostUsed: string[]} => {
  const areaCoveredByColors: Record<string, number> = {};

  const pageBackgroundColorAreas = getPageBackgroundColorsAndAreas(page);
  for (const [hex, area] of Object.entries(pageBackgroundColorAreas)) {
    areaCoveredByColors[hex] = (areaCoveredByColors[hex] || 0) + area;
  }

  for (const item of page.items.getItems()) {
    const colorAreas = getColorsAndPrioritiesFromItem(item);
    for (const [hex, area] of Object.entries(colorAreas)) {
      areaCoveredByColors[hex] = (areaCoveredByColors[hex] || 0) + area;
    }
  }

  const all = Object.entries(areaCoveredByColors)
    .sort((a, b) => b[1] - a[1])
    .map(([hex]) => hex);

  const nMostUsed = all.slice(0, n);

  return {
    allColors: all,
    nMostUsed: nMostUsed,
  };
};

const getOnetoOneColorMappingsForNMostRepeatedColors = (currentPage: Page, palette: RGB[]): Record<string, RGB> => {
  const paletteHexStrings = palette.map((color) => {
    return rgbToHexString(color);
  });

  const {allColors, nMostUsed} = getColorsOnCanvas(palette.length, currentPage);

  return isSubset(allColors, paletteHexStrings)
    ? generateOffsetColorReplacements(palette, paletteHexStrings)
    : mapSortedCanvasColorsToPalette(sortColorsByPerceptualLightness(nMostUsed), palette);
};

const generateOffsetColorReplacements = (palette: RGB[], paletteHexStrings: string[]): Record<string, RGB> => {
  const replacements: Record<string, RGB> = {};
  for (const color of palette) {
    const hex = rgbToHexString(color);
    const offsetIndex = (paletteHexStrings.indexOf(hex) + 1) % paletteHexStrings.length;
    replacements[hex] = palette[offsetIndex];
  }
  return replacements;
};

const getColorsBehindItem = (item: ItemType, page: Page, itemUpdateMap: ItemUpdateMap, pageColors: RGB[]): RGB[] => {
  const itemBelow = getItemWithFillUnderCurrentItemCenter(item, page);
  return itemBelow ? itemUpdateMap[itemBelow.uid]?.colorsUsedFromPalette || [] : pageColors;
};

const filterUsedColorsFromPalette = (palette: RGB[], usedColors: RGB[]): RGB[] => {
  return palette.filter((color) => !usedColors.some((used) => isSameColor(color, used)));
};

const applyUpdates = async (page: Page, backgroundUpdate: DeepPartial<PageBackgroundObject>, itemUpdateMap: ItemUpdateMap): Promise<void> => {
  const updates: Promise<void>[] = [];

  updates.push(page.background.updateFromObject(backgroundUpdate, {undoable: false, updateRedux: false, fireModifiedEvent: false}));

  for (const item of page.items.getItemsInOrder()) {
    const updateObject = itemUpdateMap[item.uid].updateObject;

    if (item.isTranscript()) {
      updates.push(item.updateAllSubtitlesStylesFromSubtitleObject(updateObject, {undoable: false, updateRedux: false, fireModifiedEvent: false}));
    } else {
      updates.push(item.updateFromObject(updateObject, {undoable: false, updateRedux: false, fireModifiedEvent: false}));
    }
  }

  await Promise.all(updates);
};

const getShadowColorsAndAreas = (item: ItemType): Record<string, number> => {
  const shadow = item.fabricObject.shadow;

  if (!shadow || shadow.isLightShadow() || shadow.isStrongShadow()) {
    return {};
  }

  const shadowColor = colorToRgbaArray(shadow.color);

  const boundingRect = item.fabricObject.getBoundingRect();

  const shadowArea = boundingRect.width * boundingRect.height * SHADOW_AREA_TO_ITEM_AREA_IMPORTANCE_RATIO;

  const colorHex = rgbToHexString(rgbFromRgba(shadowColor));

  return {
    [colorHex]: Math.round(shadowArea),
  };
};

const calculateRectangularStrokeArea = (dimensions: {width: number; height: number}, strokeWidth: number): number => {
  const {width, height} = dimensions;
  const outerArea = width * height;

  const innerWidth = width - 2 * strokeWidth;
  const innerHeight = height - 2 * strokeWidth;
  const innerArea = innerWidth * innerHeight;

  return outerArea - innerArea;
};

const getStrokeColorsAndAreas = (item: ItemType): Record<string, number> => {
  // if(item.isText() || )

  if (!item.border.hasBorder()) {
    return {};
  }

  const boundingRect = item.fabricObject.getBoundingRect();
  const strokeWidth = item.border.solidBorderThickness;
  const strokeArea = calculateRectangularStrokeArea(boundingRect, strokeWidth);

  const strokeColorRgba = item.border.solidBorderColor;
  const colorHex = rgbToHexString(rgbFromRgba(strokeColorRgba));

  return {
    [colorHex]: Math.round(strokeArea),
  };
};

const getFillAndBackgroundColorsAndAreas = (item: ItemType): Record<string, number> => {
  const colorsAndAreas: Record<string, number> = {};

  const boundingRect = item.fabricObject.getBoundingRect();

  if (item.isVector() || item.isRectangle()) {
    const itemFill = convertFabricColorToFillObject(item.fabricObject.fill as TSerializedFabricColor);
    const fillType = itemFill.fillType;
    if (fillType === FillTypes.NONE) {
      return {};
    }

    const fillColors = itemFill.fillColor;

    let areaToUse = 0;
    if (fillType === FillTypes.LINEAR_GRADIENT) {
      areaToUse = Math.round(boundingRect.width * (boundingRect.height / 2));
    } else if (fillType === FillTypes.RADIAL_GRADIENT) {
      areaToUse = Math.round((boundingRect.width / 2) * boundingRect.height);
    } else if (fillType === FillTypes.SOLID) {
      areaToUse = Math.round(boundingRect.width * boundingRect.height);
    }

    if (areaToUse > 0) {
      for (const color of fillColors) {
        const hex = rgbToHexString(rgbFromRgba(color));
        colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + areaToUse;
      }
    }
  }

  if (item.isTableItem()) {
    const backgroundColorType = item.backgroundType;
    if (backgroundColorType === LayoutBackgroundTypes.COLOR) {
      const bgColorHex = rgbToHexString(rgbFromRgba(item.itemBackgroundColor));
      const areaToUse = Math.round(boundingRect.width * boundingRect.height);

      colorsAndAreas[bgColorHex] = (colorsAndAreas[bgColorHex] || 0) + areaToUse;
    }
  }

  if (item.isFancyText()) {
    const fillColors = item.colors;

    if (fillColors.length > 0) {
      const areaToUseForFill = Math.round((boundingRect.width * boundingRect.height) / fillColors.length);

      for (const color of fillColors) {
        const hex = rgbToHexString(rgbFromRgba(color));
        colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + areaToUseForFill;
      }
    }
  }

  if (item.isText() || item.isTranscript()) {
    let fillType;
    let fillColors;

    if (item.isText()) {
      fillType = item.textStyles.fill.fillType;
      fillColors = item.textStyles.fill.getFillColor();
    } else {
      fillType = item.getAnySubtitle().sentenceTextStyles.fill.fillType;
      fillColors = item.getAnySubtitle().sentenceTextStyles.fill.getFillColor();
    }

    let areaToUseForFill = 0;

    if (fillType === FillTypes.LINEAR_GRADIENT) {
      areaToUseForFill = Math.round(boundingRect.width * (boundingRect.height / 2));
    } else if (fillType === FillTypes.RADIAL_GRADIENT) {
      areaToUseForFill = Math.round((boundingRect.width / 2) * boundingRect.height);
    } else if (fillType === FillTypes.SOLID) {
      areaToUseForFill = Math.round(boundingRect.width * boundingRect.height);
    }

    if (areaToUseForFill > 0) {
      for (const color of fillColors) {
        const hex = rgbToHexString(rgbFromRgba(color));
        colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + areaToUseForFill;
      }
    }

    let areaToUseForBackground = 0;

    const backgroundFill = item.isTranscript() ? item.getAnySubtitle().backgroundFill : item.background.fill;
    const backgroundColorType = backgroundFill.fillType;

    const backgroundFillColors = backgroundFill.getFillColor();

    if (backgroundColorType === FillTypes.LINEAR_GRADIENT) {
      areaToUseForBackground = Math.round(boundingRect.width * (boundingRect.height / 2));
    } else if (backgroundColorType === FillTypes.RADIAL_GRADIENT) {
      areaToUseForBackground = Math.round((boundingRect.width / 2) * boundingRect.height);
    } else if (backgroundColorType === FillTypes.SOLID) {
      areaToUseForBackground = Math.round(boundingRect.width * boundingRect.height);
    }

    if (areaToUseForBackground > 0) {
      for (const color of backgroundFillColors) {
        const hex = rgbToHexString(rgbFromRgba(color));
        colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + areaToUseForBackground;
      }
    }
  }

  if ('effects' in item && item.effects.tint) {
    const tintColorHex = rgbToHexString(rgbFromRgba(item.effects.tintColor));
    const areaToUse = Math.round(boundingRect.width * boundingRect.height);

    colorsAndAreas[tintColorHex] = (colorsAndAreas[tintColorHex] || 0) + areaToUse;
  }

  return colorsAndAreas;
};

// this function handles fills that are not relevant to items that return true for isHighContrastItem()
// basically, we don't care about the text fill colors (e.g.) because they get picked based on what the
// highest contrast color for them is relative to the item behind it
const getColorsAndPrioritiesFromItem = (item: ItemType): Record<string, number> => {
  const colorsAndAreas: Record<string, number> = {};

  const strokeColors = getStrokeColorsAndAreas(item);
  for (const [hex, area] of Object.entries(strokeColors)) {
    colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + area;
  }

  const shadowColors = getShadowColorsAndAreas(item);
  for (const [hex, area] of Object.entries(shadowColors)) {
    colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + area;
  }

  const fillAndBackgroundColors = getFillAndBackgroundColorsAndAreas(item);
  for (const [hex, area] of Object.entries(fillAndBackgroundColors)) {
    colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + area;
  }

  return colorsAndAreas;
};

const getPageBackgroundColorsAndAreas = (page: Page): Record<string, number> => {
  const colorsAndAreas: Record<string, number> = {};

  if (isImageBackground(page.background.details) && page.background.details.effects.tint) {
    const pageArea = Math.round(page.poster.width * page.poster.height);
    const pageBackgroundTintColor = rgbToHexString(rgbFromRgba(page.background.details.effects.tintColor));
    colorsAndAreas[pageBackgroundTintColor] = (colorsAndAreas[pageBackgroundTintColor] || 0) + pageArea;
  }

  if (isColorBackground(page.background.details)) {
    const fill = page.background.details.fill;
    const fillType = fill.fillType;
    const fillColors = fill.fillColor;

    let pageArea = 0;

    if (fillType === FillTypes.LINEAR_GRADIENT) {
      pageArea = Math.round(page.poster.width * (page.poster.height / 2));
    } else if (fillType === FillTypes.RADIAL_GRADIENT) {
      pageArea = Math.round((page.poster.width / 2) * page.poster.height);
    } else if (fillType === FillTypes.SOLID) {
      pageArea = Math.round(page.poster.width * page.poster.height);
    }

    if (pageArea > 0) {
      for (const color of fillColors) {
        const hex = rgbToHexString(rgbFromRgba(color));
        colorsAndAreas[hex] = (colorsAndAreas[hex] || 0) + pageArea;
      }
    }
  }

  return colorsAndAreas;
};

const getUpdatedColors = (colorsToReplace: RGBA[], colorReplacements: Record<string, RGB>, filteredPaletteToUse: RGB[], originalPalette: RGB[]): RGBA[] => {
  const updated: RGBA[] = [];
  let palette = filteredPaletteToUse;

  if (filteredPaletteToUse.length === 0) {
    palette = originalPalette;
  }

  for (const color of colorsToReplace) {
    const replacement = colorReplacements[rgbToHexString(rgbFromRgba(color))];
    if (replacement && palette.includes(replacement)) {
      // if the color exists in the one-to-one mapping made at the start, BUT, has been used by the
      // item directly behind it, then we can't use that same color again or there will be no contrast between these 2 items
      updated.push([replacement[0], replacement[1], replacement[2], color[3]]);
    } else {
      const bestMatch = findClosestColorInPaletteByLab([color[0], color[1], color[2]], palette);
      updated.push([bestMatch[0], bestMatch[1], bestMatch[2], color[3]]);
    }
  }

  return updated;
};

const getItemWithFillUnderCurrentItemCenter = (item: ItemType, page: Page): ItemType | null => {
  const canvas = page.fabricCanvas;
  const center = item.fabricObject.getCenterPoint();
  const point = new Point(center.x, center.y);
  const objects = canvas.getObjects();

  const itemIndex = objects.indexOf(item.fabricObject);

  const hiddenObjects: FabricObject[] = [];
  for (let i = itemIndex; i < objects.length; i++) {
    const obj = objects[i];
    if (obj.visible) {
      obj.visible = false;
      hiddenObjects.push(obj);
    }
  }

  let foundItem: ItemType | null = null;

  for (let i = objects.length - 1; i >= 0; i--) {
    const obj = objects[i];
    if (!obj.visible || !obj.containsPoint(point)) {
      continue;
    }

    const uid = obj.__PMWID;
    if (!uid) {
      continue;
    }

    const candidateItem = page.items.getItem(uid);
    if (candidateItem && !isHighContrastItem(candidateItem) && doesItemHaveColorToForceContrast(candidateItem)) {
      foundItem = candidateItem;
      break;
    }
  }

  for (const obj of hiddenObjects) {
    obj.visible = true;
  }

  return foundItem;
};

const getUpdateObjectAndUsedPaletteColorsForItem = (
  item: ItemType,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  originalPalette: RGB[],
  options?: {
    detectContrast?: boolean;
    colorsBehind?: RGB[];
  }
): {updateObject: DeepPartial<ItemObject>; colorsUsedFromPalette: RGB[]} => {
  const colorsUsedFromPalette: RGB[] = [];
  let updateObject: DeepPartial<ItemObject> = {};

  if (options?.detectContrast && isHighContrastItem(item) && options.colorsBehind && options.colorsBehind.length > 0) {
    const averageLab = getAverageLabColor(options.colorsBehind);
    const contrastColor = getMostContrastingColorFromPalette(originalPalette, averageLab, options.colorsBehind);

    return getUpdateObjectAndUsedPaletteColorsForItem(item, {}, [contrastColor], originalPalette);
  }

  if (item.isSlideshow()) {
    const updatedSlidesHashMap: Record<string, DeepPartial<SlideItemObject>> = {};
    const slides = item.slides.slidesHashMap;

    for (const [uid, slideItem] of Object.entries(slides)) {
      updatedSlidesHashMap[uid] = getUpdateObjectAndUsedPaletteColorsForItem(slideItem, colorReplacements, filteredPaletteToUse, originalPalette).updateObject;
    }

    updateObject = {
      slides: {
        slidesHashMap: {
          ...updatedSlidesHashMap,
        },
      },
    };
  } else if (item.isText() || item.isTextSlide()) {
    updateObject = {
      ...updateObject,
      ...getUpdateObjectForTextItem(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  } else if (item.isTableItem()) {
    updateObject = {
      ...updateObject,
      ...getUpdateObjectForTableItem(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  } else if (item.isTab()) {
    updateObject = {
      ...updateObject,
      ...getUpdateObjectForTabItem(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  } else if (item.isMenu()) {
    updateObject = {
      ...updateObject,
      ...getUpdateObjectForMenuItem(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  } else if (item.isQRItem()) {
    updateObject = {
      ...updateObject,
      ...getUpdateObjectForQrItem(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  } else if (item.isFancyText()) {
    updateObject = {
      ...updateObject,
      ...getUpdateObjectForFancyTextItem(item, colorReplacements, filteredPaletteToUse, originalPalette),
    };
  } else if (item.isTranscript()) {
    updateObject = {
      ...updateObject,
      ...getUpdateObjectForTranscriptItem(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  } else if (item.fabricObject.fill !== null && (item.isVector() || item.isRectangle() || item.isLine())) {
    updateObject = {
      ...updateObject,
      ...getShapeFillUpdateObject(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  } else if ('effects' in item && item.effects.tint) {
    updateObject = {
      ...updateObject,
      ...getTintEffectUpdateObject(item, colorReplacements, filteredPaletteToUse, colorsUsedFromPalette, originalPalette),
    };
  }

  if (item.border.hasBorder()) {
    updateObject = {
      ...updateObject,
      ...getBorderUpdateObject(item, colorReplacements, filteredPaletteToUse, originalPalette),
    };
  }

  if (item.fabricObject.shadow && (!item.fabricObject.shadow.isStrongShadow() || !item.fabricObject.shadow.isLightShadow())) {
    updateObject = {
      ...updateObject,
      ...getShadowUpdateObject(item, colorReplacements, filteredPaletteToUse, originalPalette),
    };
  }

  return {
    updateObject,
    colorsUsedFromPalette,
  };
};

const getUpdateObjectForTableItem = (
  item: TableItem,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<TableItemObject> => {
  const result: DeepPartial<TableItemObject> = {};
  const updatedTextStyles: DeepPartial<TextStylesObject> = {};

  let paletteForTextFill = [...filteredPaletteToUse];
  let paletteForSchedule = [...filteredPaletteToUse];
  let paletteForTextStroke = [...filteredPaletteToUse];

  // 1. Handle background color
  if (item.backgroundType !== LayoutBackgroundTypes.NONE) {
    const updatedBgRgba = getUpdatedColors([item.itemBackgroundColor], colorReplacements, filteredPaletteToUse, originalPalette);
    const updatedBgRgb = updatedBgRgba.map(rgbFromRgba);

    result.itemBackgroundColor = updatedBgRgba[0];
    colorsUsedFromPalette.push(...updatedBgRgb);

    // Reset text palette to full palette, minus background color
    paletteForTextFill = [...originalPalette];
    paletteForSchedule = [...originalPalette];
    paletteForTextStroke = [...originalPalette];
    removeColorsFromPalette(paletteForTextFill, updatedBgRgb);
    removeColorsFromPalette(paletteForSchedule, updatedBgRgb);
    removeColorsFromPalette(paletteForTextStroke, updatedBgRgb);
  }

  // 2. Handle schedule-specific highlight styles
  if (item.isSchedule()) {
    // Update highlighted background
    const updatedHighlightBg = getUpdatedColors([item.highlightedBackgroundColor], colorReplacements, paletteForSchedule, originalPalette);
    const updatedHighlightRgb = updatedHighlightBg.map(rgbFromRgba);

    result.highlightedBackgroundColor = updatedHighlightBg[0];
    removeColorsFromPalette(paletteForTextStroke, [updatedHighlightRgb[0]]);

    // Determine contrasting color for highlighted text
    const highlightContrastColor = getMostContrastingColorFromPalette(originalPalette, getAverageLabColor(updatedHighlightRgb), updatedHighlightRgb);

    // Update highlighted text color
    result.highlightedTextColor = getUpdatedColors([item.highlightedTextColor], {}, [highlightContrastColor], originalPalette)[0];

    // Remove used contrast color from remaining palettes
    removeColorsFromPalette(paletteForTextStroke, [highlightContrastColor]);
  }

  // 3. Handle main text fill
  const hasTextFill = item.textStyles.fill.fillType !== FillTypes.NONE;
  if (hasTextFill) {
    const fillPalette = result.itemBackgroundColor
      ? [getMostContrastingColorFromPalette(originalPalette, getAverageLabColor(paletteForTextFill), [rgbFromRgba(result.itemBackgroundColor)])]
      : paletteForTextFill;

    const updatedFillRgba = getUpdatedColors(item.textStyles.fill.fillColor, colorReplacements, fillPalette, originalPalette);
    const updatedFillRgb = updatedFillRgba.map((color) => {
      return rgbFromRgba(color);
    });

    updatedTextStyles.fill = {fillColor: updatedFillRgba};
    removeColorsFromPalette(paletteForTextStroke, updatedFillRgb);
  }

  // 4. Handle text stroke
  const hasStroke = item.textStyles.stroke && item.textStyles.strokeColor;
  if (hasStroke) {
    updatedTextStyles.strokeColor = getUpdatedColors([item.textStyles.strokeColor], {}, paletteForTextStroke, originalPalette)[0];
  }

  // 5. Apply text styles if updated
  if (Object.keys(updatedTextStyles).length > 0) {
    result.textStyles = updatedTextStyles;
  }

  return result;
};

const getUpdateObjectForTabItem = (
  item: TabsItem,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<TableItemObject> => {
  const result: DeepPartial<TabsItemObject> = {};
  const updatedTextStyles: DeepPartial<TextStylesObject> = {};

  let paletteForTextFill = [...filteredPaletteToUse];
  let paletteForTextStroke = [...filteredPaletteToUse];

  // 1. Handle background color
  if (item.backgroundType !== LayoutBackgroundTypes.NONE) {
    const updatedBgRgba = getUpdatedColors([item.itemBackgroundColor], colorReplacements, filteredPaletteToUse, originalPalette);
    const updatedBgRgb = updatedBgRgba.map((color) => {
      return rgbFromRgba(color);
    });

    result.itemBackgroundColor = updatedBgRgba[0];
    colorsUsedFromPalette.push(...updatedBgRgb);

    // Reset text palette to full palette, minus background color
    paletteForTextFill = [...originalPalette];
    paletteForTextStroke = [...originalPalette];
    removeColorsFromPalette(paletteForTextFill, updatedBgRgb);
    removeColorsFromPalette(paletteForTextStroke, updatedBgRgb);
  }

  // 2. Handle separator color
  if (item.separatorType !== SeparatorType.NONE && item.separatorColor) {
    const fillPalette = result.itemBackgroundColor
      ? [getMostContrastingColorFromPalette(originalPalette, getAverageLabColor(paletteForTextFill), [rgbFromRgba(result.itemBackgroundColor)])]
      : paletteForTextFill;

    result.separatorColor = getUpdatedColors([item.separatorColor], colorReplacements, fillPalette, originalPalette)[0];
  }

  // 3. Handle main text fill
  const hasTextFill = item.textStyles.fill.fillType !== FillTypes.NONE;
  if (hasTextFill) {
    const fillPalette = result.itemBackgroundColor
      ? [getMostContrastingColorFromPalette(originalPalette, getAverageLabColor(paletteForTextFill), [rgbFromRgba(result.itemBackgroundColor)])]
      : paletteForTextFill;

    const updatedFillRgba = getUpdatedColors(item.textStyles.fill.fillColor, colorReplacements, fillPalette, originalPalette);
    const updatedFillRgb = updatedFillRgba.map((color) => {
      return rgbFromRgba(color);
    });

    updatedTextStyles.fill = {fillColor: updatedFillRgba};
    removeColorsFromPalette(paletteForTextStroke, updatedFillRgb);
  }

  // 4. Handle text stroke
  const hasStroke = item.textStyles.stroke && item.textStyles.strokeColor;
  if (hasStroke) {
    updatedTextStyles.strokeColor = getUpdatedColors([item.textStyles.strokeColor], {}, paletteForTextStroke, originalPalette)[0];
  }

  // 5. Apply text styles if updated
  if (Object.keys(updatedTextStyles).length > 0) {
    result.textStyles = updatedTextStyles;
  }

  return result;
};

const getUpdateObjectForMenuItem = (
  item: MenuItem,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<TableItemObject> => {
  const result: DeepPartial<MenuItemObject> = {};
  const updatedTextStyles: DeepPartial<TextStylesObject> = {};

  let paletteForTextFill = [...filteredPaletteToUse];
  let paletteForTextStroke = [...filteredPaletteToUse];

  // 1. Handle background color
  if (item.backgroundType !== LayoutBackgroundTypes.NONE) {
    const updatedBgRgba = getUpdatedColors([item.itemBackgroundColor], colorReplacements, filteredPaletteToUse, originalPalette);
    const updatedBgRgb = updatedBgRgba.map((color) => {
      return rgbFromRgba(color);
    });

    result.itemBackgroundColor = updatedBgRgba[0];
    colorsUsedFromPalette.push(...updatedBgRgb);

    // Reset text palette to full palette, minus background color
    paletteForTextFill = [...originalPalette];
    paletteForTextStroke = [...originalPalette];
    removeColorsFromPalette(paletteForTextFill, updatedBgRgb);
    removeColorsFromPalette(paletteForTextStroke, updatedBgRgb);
  }

  // 3. Handle main text fill
  const hasTextFill = item.textStyles.fill.fillType !== FillTypes.NONE;
  if (hasTextFill) {
    const fillPalette = result.itemBackgroundColor
      ? [getMostContrastingColorFromPalette(originalPalette, getAverageLabColor(paletteForTextFill), [rgbFromRgba(result.itemBackgroundColor)])]
      : paletteForTextFill;

    const updatedFillRgba = getUpdatedColors(item.textStyles.fill.fillColor, colorReplacements, fillPalette, originalPalette);
    const updatedFillRgb = updatedFillRgba.map((color) => {
      return rgbFromRgba(color);
    });

    updatedTextStyles.fill = {fillColor: updatedFillRgba};
    removeColorsFromPalette(paletteForTextStroke, updatedFillRgb);
  }

  if (item.iconsColor) {
    const fillPalette = result.itemBackgroundColor
      ? [getMostContrastingColorFromPalette(originalPalette, getAverageLabColor(paletteForTextFill), [rgbFromRgba(result.itemBackgroundColor)])]
      : paletteForTextFill;

    result.iconsColor = getUpdatedColors(item.textStyles.fill.fillColor, colorReplacements, fillPalette, originalPalette)[0];
  }

  // 4. Handle text stroke
  const hasStroke = item.textStyles.stroke && item.textStyles.strokeColor;
  if (hasStroke) {
    updatedTextStyles.strokeColor = getUpdatedColors([item.textStyles.strokeColor], {}, paletteForTextStroke, originalPalette)[0];
  }

  // 5. Apply text styles if updated
  if (Object.keys(updatedTextStyles).length > 0) {
    result.textStyles = updatedTextStyles;
  }

  return result;
};

const getUpdateObjectForQrItem = (
  item: QRCodeItem,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<QRCodeItemObject> => {
  const result: DeepPartial<QRCodeItemObject> = {};

  const paletteForForegroundColor = [...filteredPaletteToUse];

  // 1. Handle background color
  if (!item.isBackgroundTransparent) {
    const updatedBgRgba = getUpdatedColors([item.qrBackgroundColor], colorReplacements, filteredPaletteToUse, originalPalette);
    const updatedBgRgb = updatedBgRgba.map((color) => {
      return rgbFromRgba(color);
    });

    result.qrBackgroundColor = updatedBgRgba[0];
    colorsUsedFromPalette.push(...updatedBgRgb);

    removeColorsFromPalette(paletteForForegroundColor, updatedBgRgb);
  }

  // 2. foreground color

  const palette = result.qrBackgroundColor
    ? [getMostContrastingColorFromPalette(originalPalette, getAverageLabColor(paletteForForegroundColor), [rgbFromRgba(result.qrBackgroundColor)])]
    : paletteForForegroundColor;

  const updatedForegroundRgba = getUpdatedColors([item.qrForegroundColor], colorReplacements, palette, originalPalette);

  result.qrForegroundColor = updatedForegroundRgba[0];

  return result;
};

const getUpdateObjectForFancyTextItem = (
  item: FancyTextItem,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  originalPalette: RGB[]
): DeepPartial<FancyTextItemObject> => {
  const result: DeepPartial<FancyTextItemObject> = {};
  const resultColors: RGBA[] = [];

  const paletteForFill2 = [...filteredPaletteToUse];
  const paletteForOutline = [...filteredPaletteToUse];

  const fillColor1 = item.colors[0];
  const fillColor2 = item.colors[1];
  const outline = item.colors.length > 2 ? item.colors[2] : undefined;

  const updatedFill1Rgba = getUpdatedColors([fillColor1], colorReplacements, filteredPaletteToUse, originalPalette);
  resultColors.push(updatedFill1Rgba[0]);
  const updatedFill1Rgb = updatedFill1Rgba.map((color) => {
    return rgbFromRgba(color);
  });
  removeColorsFromPalette(paletteForFill2, updatedFill1Rgb);
  removeColorsFromPalette(paletteForOutline, updatedFill1Rgb);

  const updatedFill2Rgba = getUpdatedColors([fillColor2], {}, paletteForFill2, originalPalette);
  resultColors.push(updatedFill2Rgba[0]);
  const updatedFill2Rgb = updatedFill2Rgba.map((color) => {
    return rgbFromRgba(color);
  });
  removeColorsFromPalette(paletteForOutline, updatedFill2Rgb);

  if (outline) {
    const updatedOutlineRgba = getUpdatedColors([outline], {}, paletteForOutline, originalPalette);
    resultColors.push(updatedOutlineRgba[0]);
  }

  result.colors = resultColors;

  return result;
};

const getUpdateObjectForTextItem = (
  item: TextItem,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<TextItemObject> => {
  const result: DeepPartial<TextItemObject> = {};
  const updatedTextStyles: DeepPartial<TextStylesObject> = {};

  let paletteForTextFill = [...filteredPaletteToUse];
  let paletteForTextStroke = [...filteredPaletteToUse];

  if (item.background.fill.hasFill()) {
    const updatedRgba = getUpdatedColors(item.background.fill.getFillColor(), colorReplacements, filteredPaletteToUse, originalPalette);
    const updatedRgb = updatedRgba.map((color) => {
      return rgbFromRgba(color);
    });

    colorsUsedFromPalette.push(...updatedRgb);

    paletteForTextFill = [...originalPalette];
    paletteForTextStroke = [...originalPalette];
    removeColorsFromPalette(paletteForTextFill, updatedRgb);
    removeColorsFromPalette(paletteForTextStroke, updatedRgb);

    result.background = {
      fill: {
        fillColor: updatedRgba,
      },
    };
  }

  if (item.textStyles.fill.fillType !== FillTypes.NONE) {
    const bgFillColorsRgba = result?.background?.fill?.fillColor;
    const fillPalette =
      bgFillColorsRgba && item.textStyles.fill.fillColor.length < 1
        ? [
            getMostContrastingColorFromPalette(
              originalPalette,
              getAverageLabColor(paletteForTextFill),
              bgFillColorsRgba.map((color) => {
                return rgbFromRgba(color);
              })
            ),
          ]
        : paletteForTextFill;

    const updatedRgba = getUpdatedColors(item.textStyles.fill.fillColor, colorReplacements, fillPalette, originalPalette);

    const updatedRgb = updatedRgba.map((color) => {
      return rgbFromRgba(color);
    });
    removeColorsFromPalette(paletteForTextStroke, updatedRgb);

    updatedTextStyles.fill = {fillColor: updatedRgba};

    updateLastAddedTextColor(updatedRgba[0]);
  }

  if (item.textStyles.stroke && item.textStyles.strokeColor) {
    const updated = getUpdatedColors([item.textStyles.strokeColor], colorReplacements, paletteForTextStroke, originalPalette);
    updatedTextStyles.strokeColor = updated[0];
  }

  if (updatedTextStyles.fill || updatedTextStyles.strokeColor) {
    result.textStyles = updatedTextStyles;
  }

  return result;
};

const getUpdateObjectForTranscriptItem = (
  item: TranscriptItem,
  colorReplacements: Record<string, RGB>,
  filteredPaletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<SubtitleObject> => {
  const subtitle = item.getAnySubtitle();
  const updatedSubtitleStyles: DeepPartial<SubtitleObject> = {};
  const updatedSubtitleSentenceTextStyles: DeepPartial<TextStylesObject> = {};
  const updatedSubtitleActiveWordTextStyles: DeepPartial<TextStylesObject> = {};

  let paletteForSentenceTextStyles = [...filteredPaletteToUse];
  let paletteForActiveWordTextStyles = [...filteredPaletteToUse];
  let paletteForSentenceTextStroke = [...filteredPaletteToUse];
  let paletteForActiveWordTextStroke = [...filteredPaletteToUse];

  // ignore gradients in transcript items for simplicity
  if (subtitle.backgroundFill.hasFill()) {
    if (subtitle.backgroundFill.fillType !== FillTypes.NONE) {
      const updatedBackgroundFillRgba = getUpdatedColors([subtitle.backgroundFill.getFillColor()[0]], colorReplacements, filteredPaletteToUse, originalPalette)[0];
      const updatedBackgroundFillRgb = rgbFromRgba(updatedBackgroundFillRgba);
      colorsUsedFromPalette.push(updatedBackgroundFillRgb);

      updatedSubtitleStyles.backgroundFill = {
        fillColor: [updatedBackgroundFillRgba],
      };

      paletteForSentenceTextStyles = [...originalPalette];
      paletteForActiveWordTextStyles = [...originalPalette];
      paletteForSentenceTextStroke = [...originalPalette];
      paletteForActiveWordTextStroke = [...originalPalette];

      removeColorsFromPalette(paletteForSentenceTextStyles, [updatedBackgroundFillRgb]);
      removeColorsFromPalette(paletteForActiveWordTextStyles, [updatedBackgroundFillRgb]);
      removeColorsFromPalette(paletteForSentenceTextStroke, [updatedBackgroundFillRgb]);
      removeColorsFromPalette(paletteForActiveWordTextStroke, [updatedBackgroundFillRgb]);
    }
  }

  const updatedSentenceTextStylesRgba = getUpdatedColors(subtitle.sentenceTextStyles.fill.fillColor, colorReplacements, paletteForSentenceTextStyles, originalPalette)[0];
  const updatedSentenceTextStylesRgb = rgbFromRgba(updatedSentenceTextStylesRgba);

  removeColorsFromPalette(paletteForSentenceTextStroke, [updatedSentenceTextStylesRgb]);
  removeColorsFromPalette(paletteForActiveWordTextStyles, [updatedSentenceTextStylesRgb]);

  updatedSubtitleSentenceTextStyles.fill = {
    fillColor: [updatedSentenceTextStylesRgba],
  };

  if (subtitle.sentenceTextStyles.stroke) {
    updatedSubtitleSentenceTextStyles.strokeColor = getUpdatedColors(
      [subtitle.sentenceTextStyles.strokeColor],
      colorReplacements,
      paletteForSentenceTextStroke,
      originalPalette
    )[0];
  }

  if (subtitle.animationStyle !== SubtitleTemplateType.SIMPLE) {
    const updatedActiveWordTextStylesRgba = getUpdatedColors(subtitle.activeWordTextStyles.fill.fillColor, colorReplacements, paletteForActiveWordTextStyles, originalPalette)[0];
    const updatedActiveWordTextStylesRgb = rgbFromRgba(updatedActiveWordTextStylesRgba);

    removeColorsFromPalette(paletteForActiveWordTextStroke, [updatedActiveWordTextStylesRgb]);

    updatedSubtitleActiveWordTextStyles.fill = {
      fillColor: [updatedActiveWordTextStylesRgba],
    };

    if (subtitle.activeWordTextStyles.stroke) {
      updatedSubtitleActiveWordTextStyles.strokeColor = getUpdatedColors(
        [subtitle.activeWordTextStyles.strokeColor],
        colorReplacements,
        paletteForActiveWordTextStroke,
        originalPalette
      )[0];
    }
  }

  return {...updatedSubtitleStyles, sentenceTextStyles: {...updatedSubtitleSentenceTextStyles}, activeWordTextStyles: {...updatedSubtitleActiveWordTextStyles}};
};

const getShapeFillUpdateObject = (
  item: ItemType,
  colorReplacements: Record<string, RGB>,
  paletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<ItemObject> => {
  if (!(item.isRectangle() || item.isVector())) {
    return {};
  }

  const fillObj = convertFabricColorToFillObject(item.fabricObject.fill as TSerializedFabricColor);

  if (fillObj.fillType === FillTypes.NONE) return {};

  const updatedRgba = getUpdatedColors(fillObj.fillColor, colorReplacements, paletteToUse, originalPalette);
  colorsUsedFromPalette.push(
    ...updatedRgba.map((color) => {
      return rgbFromRgba(color);
    })
  );

  return {
    fill: convertFillObjectToFabricColor({
      fillColor: updatedRgba,
      fillType: fillObj.fillType,
    }),
  };
};

const getTintEffectUpdateObject = (
  item: ItemType,
  colorReplacements: Record<string, RGB>,
  paletteToUse: RGB[],
  colorsUsedFromPalette: RGB[],
  originalPalette: RGB[]
): DeepPartial<ItemObject> => {
  if (!('effects' in item)) {
    return {};
  }

  const updated = getUpdatedColors([item.effects.tintColor], colorReplacements, paletteToUse, originalPalette);
  colorsUsedFromPalette.push(rgbFromRgba(updated[0]));

  return {
    effects: {
      tintColor: updated[0],
    },
  };
};

const getBorderUpdateObject = (item: ItemType, colorReplacements: Record<string, RGB>, paletteToUse: RGB[], originalPalette: RGB[]): DeepPartial<ItemObject> => {
  if (!item.border.hasBorder()) {
    return {};
  }

  const updated = getUpdatedColors([item.border.solidBorderColor], colorReplacements, paletteToUse, originalPalette);

  return {
    border: {
      solidBorderColor: updated[0],
    },
  };
};

const getShadowUpdateObject = (item: ItemType, colorReplacements: Record<string, RGB>, paletteToUse: RGB[], originalPalette: RGB[]): DeepPartial<ItemObject> => {
  if (!item.fabricObject.shadow) {
    return {};
  }

  const updated = getUpdatedColors([colorToRgbaArray(item.fabricObject.shadow.color)], colorReplacements, paletteToUse, originalPalette);

  return {
    shadow: {color: rgbaToHexString(updated[0])},
  };
};

const getUpdateObjectAndUsedPaletteColorsForPageBackground = (
  page: Page,
  colorReplacements: Record<string, RGB>,
  palette: RGB[]
): {updateObject: DeepPartial<PageBackgroundObject>; colorsUsedFromPalette: RGB[]} => {
  const colorsUsedFromPalette: RGB[] = [];

  let updateObject: DeepPartial<PageBackgroundObject> = {};

  if (isImageBackground(page.background.details) && page.background.details.effects.tint) {
    const tintColor = page.background.details.effects.tintColor;

    const updatedTintColorsRgba = getUpdatedColors([tintColor], colorReplacements, palette, palette);
    const updatedTintColorsRgb: RGB[] = updatedTintColorsRgba.map((rgba) => {
      return rgbFromRgba(rgba);
    });
    colorsUsedFromPalette.push(...updatedTintColorsRgb);

    updateObject = {
      ...updateObject,
      details: {
        effects: {
          tintColor: updatedTintColorsRgba[0],
        },
      },
    };
  } else if (isColorBackground(page.background.details)) {
    const pageBackgroundColorFillType = page.background.details.fill.fillType;

    if (pageBackgroundColorFillType !== FillTypes.NONE) {
      const updatedFillColors = getUpdatedColors(page.background.details.fill.fillColor, colorReplacements, palette, palette);
      const updatedFillColorsRgb: RGB[] = updatedFillColors.map((rgba) => [rgba[0], rgba[1], rgba[2]]);
      colorsUsedFromPalette.push(...updatedFillColorsRgb);

      updateObject = {
        ...updateObject,
        details: {
          fill: {
            fillColor: updatedFillColors,
          },
        },
      };
    }
  }

  return {
    updateObject,
    colorsUsedFromPalette,
  };
};

const isHighContrastItem = (item: ItemType): boolean => {
  if ('textStyles' in item) {
    if (item.isText()) {
      return !item.background.fill.hasFill();
    }
    if (item.isSchedule()) {
      return false;
    }
    if (item.isTableItem() || item.isMenu() || item.isTab()) {
      return item.backgroundType === LayoutBackgroundTypes.NONE;
    }

    return false;
  }

  if (item.isQRItem()) {
    return item.isBackgroundTransparent;
  }

  return false;
};

const doesItemHaveColorToForceContrast = (item: ItemType): boolean => {
  if ('textStyles' in item) {
    if (item.isText()) {
      return item.background.fill.hasFill();
    }
    if (item.isTableItem() || item.isMenu() || item.isTab()) {
      return item.backgroundType !== LayoutBackgroundTypes.NONE;
    }
    return false;
  }

  if ('effects' in item) {
    return item.effects.tint;
  }

  return !!item.fabricObject.fill;
};

const mapSortedCanvasColorsToPalette = (sortedColors: string[], palette: RGB[]): Record<string, RGB> => {
  const colorReplacements: Record<string, RGB> = {};

  for (let i = 0; i < sortedColors.length && i < palette.length; i++) {
    colorReplacements[sortedColors[i]] = palette[i];
  }

  return colorReplacements;
};

const removeColorsFromPalette = (palette: RGB[], colorsToRemove: RGB[]): void => {
  const removeSet = new Set(
    colorsToRemove.map((color) => {
      return rgbToHexString(color);
    })
  );

  for (let i = palette.length - 1; i >= 0; i--) {
    const hex = rgbToHexString(palette[i]);
    if (removeSet.has(hex)) {
      palette.splice(i, 1);
    }
  }
};
